// redux/slices/googleCalendarSlice.js  (WEB)
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { resetStore } from './resetStore';
import { getServerUrl, getAuthHeaders } from '../../Utils/serverSelector';

// ─── helpers ──────────────────────────────────────────────────────────────────

const cfg = async () => ({
  headers: await getAuthHeaders(),
  withCredentials: true,
  validateStatus: () => true,
});

const pickErr = (res, fallback) =>
  res?.data?.message || res?.data?.error || fallback;

// ─── thunks ───────────────────────────────────────────────────────────────────

// Подключить (web Authorization Code flow)
export const connectGoogleCalendar = createAsyncThunk(
  'googleCalendar/connect',
  async ({ uid, authCode, redirectUri }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${getServerUrl()}/google-calendar/connect`,
        { uid, code: authCode, platform: 'web', redirectUri },
        await cfg()
      );
      if (res.status !== 200 && res.status !== 201)
        throw new Error(pickErr(res, `HTTP ${res.status}`));
      return res.data;
    } catch (err) {
      console.error("❌ [Google Calendar connect error]", {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
        url: err?.config?.url,
        method: err?.config?.method,
        hasAuthorization: Boolean(
          err?.config?.headers?.Authorization ||
          err?.config?.headers?.authorization
        ),
      });

      return rejectWithValue(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Google Calendar connection failed"
      );
    }
  }
);

// Отключить
export const disconnectGoogleCalendar = createAsyncThunk(
  'googleCalendar/disconnect',
  async ({ uid }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${getServerUrl()}/google-calendar/disconnect`,
        { uid },
        await cfg()
      );
      if (res.status !== 200) throw new Error(pickErr(res, `HTTP ${res.status}`));
      return true;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Статус подключения
export const fetchGoogleCalendarStatus = createAsyncThunk(
  'googleCalendar/fetchStatus',
  async ({ uid }, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${getServerUrl()}/google-calendar/status?uid=${encodeURIComponent(uid)}`,
        await cfg()
      );
      if (res.status !== 200) throw new Error(pickErr(res, `HTTP ${res.status}`));
      return Boolean(res.data.isConnected);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Статистика синхронизации
export const fetchGoogleSyncStatus = createAsyncThunk(
  'googleCalendar/syncStatus',
  async ({ uid }, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${getServerUrl()}/google-calendar/sync/status?uid=${encodeURIComponent(uid)}`,
        await cfg()
      );
      if (res.status !== 200) throw new Error(pickErr(res, `HTTP ${res.status}`));
      return res.data; // { total, synced, unsynced, allSynced }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Push: локальные → Google
export const syncPushToGoogle = createAsyncThunk(
  'googleCalendar/syncPush',
  async ({ uid }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${getServerUrl()}/google-calendar/sync/push`,
        { uid, platform: 'web' },
        await cfg()
      );
      if (res.status !== 200) throw new Error(pickErr(res, `HTTP ${res.status}`));
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Pull: восстановить связи Google → локальные
export const syncPullFromGoogle = createAsyncThunk(
  'googleCalendar/syncPull',
  async ({ uid }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${getServerUrl()}/google-calendar/sync/pull`,
        { uid, platform: 'web' },
        await cfg()
      );
      if (res.status !== 200) throw new Error(pickErr(res, `HTTP ${res.status}`));
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Получить внешние события из всех календарей
export const fetchExternalGoogleEvents = createAsyncThunk(
  'googleCalendar/fetchExternal',
  async ({ uid, timeMin, timeMax }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${getServerUrl()}/google-calendar/sync/import-external`,
        { uid, platform: 'web', timeMin, timeMax },
        await cfg()
      );
      if (res.status !== 200) throw new Error(pickErr(res, `HTTP ${res.status}`));
      return res.data; // { events: [...], total }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);


// Скачать все события Google Calendar мастера в Excel
export const exportGoogleCalendarEventsExcel = createAsyncThunk(
  'googleCalendar/exportExcel',
  async (
    { uid, lang = 'ru', allCalendars = true, timeMin, timeMax },
    { rejectWithValue }
  ) => {
    try {
      if (!uid) throw new Error('UID не передан');

      const query = new URLSearchParams({
        lang,
        allCalendars: String(allCalendars),
      });

      if (timeMin) query.set('timeMin', timeMin);
      if (timeMax) query.set('timeMax', timeMax);

      const res = await axios.get(
        `${getServerUrl()}/google-calendar/export-excel/${encodeURIComponent(uid)}?${query.toString()}`,
        {
          ...(await cfg()),
          responseType: 'arraybuffer',
        }
      );

      if (res.status !== 200) {
        let message = `HTTP ${res.status}`;

        try {
          const decoded = new TextDecoder('utf-8').decode(res.data);
          const parsed = JSON.parse(decoded);
          message = parsed?.message || parsed?.error || message;
        } catch {
          // Сервер мог вернуть обычный текст.
        }

        throw new Error(message);
      }

      const contentDisposition = res.headers?.['content-disposition'] || '';

      const encodedFileName = contentDisposition.match(
        /filename\*=UTF-8''([^;]+)/
      )?.[1];

      const regularFileName = contentDisposition.match(
        /filename="?([^";]+)"?/
      )?.[1];

      const fileName = encodedFileName
        ? decodeURIComponent(encodedFileName)
        : regularFileName ||
          `google-calendar-events-${new Date()
            .toISOString()
            .slice(0, 10)}.xlsx`;

      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(downloadUrl);

      return {
        success: true,
        fileName,
      };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to export Google Calendar events'
      );
    }
  }
);

// Подтвердить импорт внешнего события
export const confirmGoogleEventImport = createAsyncThunk(
  'googleCalendar/confirmImport',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${getServerUrl()}/google-calendar/sync/import-confirm`,
        { ...payload, platform: 'web' },
        await cfg()
      );
      if (res.status !== 201) throw new Error(pickErr(res, `HTTP ${res.status}`));
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Обновить событие в Google Calendar при изменении встречи
export const updateGoogleCalendarEvent = createAsyncThunk(
  "googleCalendar/updateEvent",
  async ({ uid, appointmentId, platform = "web" }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      if (!appointmentId) throw new Error("appointmentId не передан");

      const res = await axios.post(
        `${getServerUrl()}/google-calendar/event/update`,
        { uid, appointmentId, platform },
        await cfg()
      );

      if (res.status !== 200) {
        throw new Error(pickErr(res, `HTTP ${res.status}`));
      }

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Update event failed"
      );
    }
  }
);
// Удалить событие из Google Calendar при удалении встречи
export const deleteGoogleCalendarEvent = createAsyncThunk(
  "googleCalendar/deleteEvent",
  async ({ uid, appointmentId, platform = "web" }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      if (!appointmentId) throw new Error("appointmentId не передан");

      const res = await axios.post(
        `${getServerUrl()}/google-calendar/event/delete`,
        {
          uid,
          appointmentId,
          platform,
        },
        await cfg()
      );

      if (res.status !== 200) {
        throw new Error(pickErr(res, `HTTP ${res.status}`));
      }

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Delete event failed"
      );
    }
  }
);
// ─── slice ────────────────────────────────────────────────────────────────────

const initialState = {
  isConnected: false,
  hasFetchedStatus: false,
  loading: false,
  syncLoading: false,
  exportLoading: false,
  error: null,
  syncStats: {
    total: 0,
    synced: 0,
    unsynced: 0,
    allSynced: true,
  },
};

const googleCalendarSlice = createSlice({
  name: 'googleCalendar',
  initialState,
  reducers: {
    resetGoogleCalendar: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // connect
      .addCase(connectGoogleCalendar.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(connectGoogleCalendar.fulfilled, (s) => { s.loading = false; s.isConnected = true; })
      .addCase(connectGoogleCalendar.rejected, (s, a) => { s.loading = false; s.error = a.payload; })

      // disconnect
      .addCase(disconnectGoogleCalendar.pending, (s) => { s.loading = true; })
      .addCase(disconnectGoogleCalendar.fulfilled, (s) => { s.loading = false; s.isConnected = false; s.syncStats = initialState.syncStats; })
      .addCase(disconnectGoogleCalendar.rejected, (s, a) => { s.loading = false; s.error = a.payload; })

      // status
      .addCase(fetchGoogleCalendarStatus.pending, (s) => { s.loading = true; })
      .addCase(fetchGoogleCalendarStatus.fulfilled, (s, a) => { s.loading = false; s.isConnected = a.payload; s.hasFetchedStatus = true; })
      .addCase(fetchGoogleCalendarStatus.rejected, (s, a) => { s.loading = false; s.error = a.payload; s.hasFetchedStatus = true; })

      // syncStatus
      .addCase(fetchGoogleSyncStatus.fulfilled, (s, a) => {
        const { total = 0, synced = 0, unsynced = 0, allSynced = true } = a.payload || {};
        s.syncStats = { total, synced, unsynced, allSynced };
      })

      // syncPush
      .addCase(syncPushToGoogle.pending, (s) => { s.syncLoading = true; s.error = null; })
      .addCase(syncPushToGoogle.fulfilled, (s) => { s.syncLoading = false; })
      .addCase(syncPushToGoogle.rejected, (s, a) => { s.syncLoading = false; s.error = a.payload; })

      // syncPull
      .addCase(syncPullFromGoogle.pending, (s) => { s.syncLoading = true; })
      .addCase(syncPullFromGoogle.fulfilled, (s) => { s.syncLoading = false; })
      .addCase(syncPullFromGoogle.rejected, (s, a) => { s.syncLoading = false; s.error = a.payload; })

      // fetchExternal / confirmImport
      .addCase(fetchExternalGoogleEvents.pending, (s) => { s.loading = true; })
      .addCase(fetchExternalGoogleEvents.fulfilled, (s) => { s.loading = false; })
      .addCase(fetchExternalGoogleEvents.rejected, (s, a) => { s.loading = false; s.error = a.payload; })
      .addCase(confirmGoogleEventImport.pending, (s) => { s.loading = true; })
      .addCase(confirmGoogleEventImport.fulfilled, (s) => { s.loading = false; })
      .addCase(confirmGoogleEventImport.rejected, (s, a) => { s.loading = false; s.error = a.payload; })

      // updateEvent
      .addCase(updateGoogleCalendarEvent.pending, (s) => {
        s.syncLoading = true;
        s.error = null;
      })
      .addCase(updateGoogleCalendarEvent.fulfilled, (s) => {
        s.syncLoading = false;
      })
      .addCase(updateGoogleCalendarEvent.rejected, (s, a) => {
        s.syncLoading = false;
        s.error = a.payload;
      })

      // deleteEvent
      .addCase(deleteGoogleCalendarEvent.pending, (s) => {
        s.syncLoading = true;
        s.error = null;
      })
      .addCase(deleteGoogleCalendarEvent.fulfilled, (s) => {
        s.syncLoading = false;
      })
      .addCase(deleteGoogleCalendarEvent.rejected, (s, a) => {
        s.syncLoading = false;
        s.error = a.payload;
      })
      // exportExcel
      .addCase(exportGoogleCalendarEventsExcel.pending, (s) => {
        s.exportLoading = true;
        s.error = null;
      })
      .addCase(exportGoogleCalendarEventsExcel.fulfilled, (s) => {
        s.exportLoading = false;
      })
      .addCase(exportGoogleCalendarEventsExcel.rejected, (s, a) => {
        s.exportLoading = false;
        s.error = a.payload;
      })

      // global reset
      .addCase(resetStore, () => initialState);
  },
});

export const { resetGoogleCalendar } = googleCalendarSlice.actions;
export default googleCalendarSlice.reducer;